import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { eventService } from '@/lib/api/services/event'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Event,
  EventStats,
  CreateEvent,
  UpdateEvent,
  UpdateEventStatus,
  EventFilterParams
} from '@/lib/api/types'

// Query keys for caching
export const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  list: (filters?: EventFilterParams) => [...eventKeys.lists(), filters] as const,
  details: () => [...eventKeys.all, 'detail'] as const,
  detail: (id: string) => [...eventKeys.details(), id] as const,
  filtered: (params: PaginationParams, filters: EventFilterParams) =>
    [...eventKeys.all, 'filtered', params, filters] as const,
  search: (query: string) => [...eventKeys.all, 'search', query] as const,
  stats: () => [...eventKeys.all, 'stats'] as const
}

// Hook to filter events with pagination
export function useFilterEvents(
  params: PaginationParams,
  filters: EventFilterParams = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<Event>>, Error>({
    queryKey: eventKeys.filtered(params, filters),
    queryFn: () => eventService.filterEvents(params, filters)
  })
}

// Hook to fetch a single event
export function useEvent(eventId?: string) {
  return useQuery<ApiResponse<Event>, Error>({
    queryKey: eventKeys.detail(eventId!),
    queryFn: () => eventService.getEventById(eventId!),
    enabled: !!eventId
  })
}

// Hook to create an event
export function useCreateEvent() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Event>, Error, CreateEvent>({
    mutationFn: (data) => eventService.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: eventKeys.all })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
    }
  })
}

// Hook to update an event
export function useUpdateEvent() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Event>,
    Error,
    { eventId: string; data: UpdateEvent }
  >({
    mutationFn: ({ eventId, data }) =>
      eventService.updateEvent(eventId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: eventKeys.all })
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(variables.eventId) })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
    }
  })
}

// Hook to update event status
export function useUpdateEventStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { eventId: string; data: UpdateEventStatus }
  >({
    mutationFn: ({ eventId, data }) =>
      eventService.updateEventStatus(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: eventKeys.all })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
    }
  })
}

// Hook to delete an event
export function useDeleteEvent() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    string
  >({
    mutationFn: (eventId) =>
      eventService.deleteEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() })
      queryClient.invalidateQueries({ queryKey: eventKeys.all })
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() })
    }
  })
}

// Hook to fetch published events count
export function usePublishedEventsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...eventKeys.stats(), 'published'],
    queryFn: () => eventService.getPublishedEventsCount()
  })
}

// Hook to fetch draft events count
export function useDraftEventsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...eventKeys.stats(), 'draft'],
    queryFn: () => eventService.getDraftEventsCount()
  })
}

// Hook to search events
export function useSearchEvents(
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<Event>>, Error>({
    queryKey: eventKeys.search(searchBy || ''),
    queryFn: () => eventService.searchEvents(searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook to fetch event stats
export function useEventStats() {
  return useQuery<EventStats, Error>({
    queryKey: eventKeys.stats(),
    queryFn: async () => {
      const response = await eventService.getEventStats()
      return response.data
    },
    staleTime: 5 * 60 * 1000
  })
}

